import { InputRef } from '@chem-po/core'
import {
  AssessmentDocument,
  FieldMap,
  Mode,
  SignableDocument,
  UserRoleItem,
  WithId,
} from '@hb/shared'
import { createContext, createRef, RefObject, useContext } from 'react'
import { EditorSize } from './hooks'

export interface DocumentFormContextValue<FieldType extends FieldMap = FieldMap> {
  toBeSignedByUser: UserRoleItem | null
  toBeSignedByMe: boolean
  signerLoading: boolean
  field: FieldType | null
  fieldsDisabled: boolean
  troubleSigningOpen: boolean
  disclaimerChecked: boolean
  setDisclaimerChecked: (checked: boolean) => void
  document: WithId<AssessmentDocument> | WithId<SignableDocument> | undefined
  termsOpen: boolean
  setTermsOpen: (open: boolean) => void
  width: number
  height: number
  registerInput: (id: string, ref: InputRef | null, container: HTMLDivElement | null) => void
  unregisterInput: (id: string) => void
  focusInput: (id: string) => void
  goNextById: (id: string) => boolean
}

export type TextEditorContextValue = {
  mode: Mode
  setMode: (mode: Mode) => void
  editorDomElement: RefObject<HTMLDivElement | null>
  document: WithId<AssessmentDocument> | WithId<SignableDocument> | null
} & EditorSize

export const TextEditorContext = createContext<TextEditorContextValue>({
  mode: 'View',
  setMode: () => {},
  editorDomElement: createRef(),
  document: null,
  baseZoom: 1,
  modalWidth: 0,
  height: 0,
  width: 0,
})

export const useTextEditor = () => useContext(TextEditorContext)

export const DocumentFormContext = createContext<DocumentFormContextValue<any>>({
  field: null,
  fieldsDisabled: false,
  toBeSignedByUser: null,
  toBeSignedByMe: false,
  signerLoading: false,
  disclaimerChecked: false,
  setDisclaimerChecked: () => {},
  termsOpen: false,
  setTermsOpen: () => {},
  width: 0,
  height: 0,
  document: undefined,
  registerInput: () => {},
  unregisterInput: () => {},
  focusInput: () => {},
  goNextById: () => false,
  troubleSigningOpen: false,
})

export const useDocumentForm = <FieldType extends FieldMap = FieldMap>() =>
  useContext<DocumentFormContextValue<FieldType>>(DocumentFormContext)
